(() => {
  if (window.CaratKinSiteExperience) return;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  /* The prototype's page-routing table lived here. It resolved every href="#"
     anchor to a ../pages/<name>/index.html file by matching the link's text. A
     Shopify theme has real URLs — routes.*, collection.url, link lists — so the
     table and the link-hydration pass that used it are both gone. Left in, they
     rewrote any link whose setting is not filled in yet (header.liquid falls back
     to "#") to a CDN path that 404s. Everything else in this file — scroll reveal,
     marquees, image motion, the page-leave fade and the section-load
     rehydration — is unchanged. */

  const selectIncludingRoot = (root, selector) => {
    const matches = root?.nodeType === Node.ELEMENT_NODE && root.matches(selector) ? [root] : [];
    return matches.concat([...(root?.querySelectorAll?.(selector) || [])]);
  };

  const revealObserver = "IntersectionObserver" in window
    ? new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -7%" })
    : null;

  function hydrateMotion(root = document) {
    const candidates = root.querySelectorAll?.([
      "main > section:not(:first-child)",
      // Shopify wraps each section in <div class="shopify-section">, so the plain
      // `main > section` above matches nothing on a live theme.
      "main > .shopify-section:not(:first-child) > section",
      ".story-page .founder-note__title",
      ".story-page .founder-note__media",
      ".story-page .founder-note__copy",
      ".final-cta",
      ".product-card",
      ".home-product-card",
      ".listing-product-card",
      ".consult-product-card",
      ".wishlist-card",
      ".collection-feature-card",
      ".store-card",
      ".testimonial-card",
      ".pdp-material-card",
      ".pdp-certification-card",
    ].join(",")) || [];

    candidates.forEach((element, index) => {
      if (element.dataset.motionReady === "true") return;
      element.dataset.motionReady = "true";
      element.classList.add("ck-reveal");
      element.style.setProperty("--ck-reveal-delay", `${Math.min(index % 4, 3) * 45}ms`);
      if (reducedMotion.matches || !revealObserver) element.classList.add("is-visible");
      else revealObserver.observe(element);
    });

    root.querySelectorAll?.([
      ".product-card__media",
      ".home-product-card__media",
      ".collection-feature-card",
      ".media-card",
      ".category-tile",
      ".editorial-grid figure",
      ".instagram-journey__feed a",
      ".pdp-look-feature",
      ".pdp-look-product__media",
      ".story-page .founder-note__media",
    ].join(",")).forEach((element) => element.classList.add("ck-image-motion"));

  }

  const marqueeRecords = new Map();
  let marqueeResizeTimer = 0;

  /* Scroll reveal and marquees do not mix. Cards inside a track are translated
     outside a clipped viewport, and the duplicated groups the marquee builds are
     never handed to the IntersectionObserver, so anything left carrying
     .ck-reveal would stay at opacity 0 for good. Settle every revealed node
     inside a track into its final state; the parent section keeps its own
     reveal animation, so the entrance motion is unchanged. */
  function settleRevealedContent(root) {
    selectIncludingRoot(root, ".ck-reveal").forEach((node) => {
      revealObserver?.unobserve(node);
      node.classList.remove("ck-reveal", "is-visible");
      node.style.removeProperty("--ck-reveal-delay");
      node.dataset.motionReady = "true";
    });
  }

  function makeCloneInert(element) {
    element.setAttribute("aria-hidden", "true");
    if (element.hasAttribute("id")) element.removeAttribute("id");
    if (element.matches("a, button, input, select, textarea, [tabindex]")) element.setAttribute("tabindex", "-1");
    element.querySelectorAll("[id]").forEach((child) => child.removeAttribute("id"));
    element.querySelectorAll("a, button, input, select, textarea, [tabindex]").forEach((child) => child.setAttribute("tabindex", "-1"));
    settleRevealedContent(element);
  }

  function prepareExistingMarquee(track, direction) {
    if (track.dataset.ckMarqueePrepared === "true") return track;
    const groups = [...track.children].filter((child) => child.nodeType === Node.ELEMENT_NODE);
    if (!groups.length) return null;

    const source = groups[0];
    groups.slice(1).forEach((group) => group.remove());
    source.dataset.ckMarqueeSource = "true";
    track.dataset.ckMarqueePrepared = "true";
    track.dataset.marqueeDirection = direction;
    track.classList.add("ck-marquee-track", "ck-seamless-marquee");
    source.classList.add("ck-marquee-group");
    track.parentElement?.classList.add("ck-marquee-viewport");
    settleRevealedContent(track);
    return track;
  }

  function prepareFlatMarquee(viewport, type, direction) {
    if (viewport.dataset.ckMarqueeWrapped === "true") return viewport.querySelector(":scope > .ck-marquee-track");
    const items = [...viewport.children];
    if (!items.length) return null;

    const track = document.createElement("div");
    const group = document.createElement("div");
    track.className = "ck-marquee-track ck-seamless-marquee";
    group.className = "ck-marquee-group";
    group.dataset.ckMarqueeSource = "true";
    items.forEach((item) => group.append(item));
    track.append(group);
    track.dataset.marqueeDirection = direction;
    track.dataset.ckMarqueePrepared = "true";
    viewport.append(track);
    viewport.dataset.ckMarqueeWrapped = "true";
    viewport.classList.add("ck-marquee-viewport", type === "benefits" ? "ck-benefits-marquee" : "ck-instagram-marquee");
    settleRevealedContent(track);
    return track;
  }

  function measureMarquee(track) {
    const viewport = track.parentElement;
    const source = track.querySelector(":scope > [data-ck-marquee-source='true']");
    if (!viewport || !source || !viewport.isConnected) return;

    track.querySelectorAll(":scope > [data-ck-marquee-copy='true']").forEach((copy) => copy.remove());
    source.querySelectorAll(":scope > [data-ck-fill-copy='true']").forEach((copy) => copy.remove());
    const originals = [...source.children];
    if (!originals.length) return;

    const minimumWidth = Math.max(viewport.clientWidth + 1, 320);
    let cycles = 0;
    while (source.scrollWidth < minimumWidth && cycles < 20) {
      originals.forEach((original) => {
        const clone = original.cloneNode(true);
        clone.dataset.ckFillCopy = "true";
        makeCloneInert(clone);
        source.append(clone);
      });
      cycles += 1;
    }

    const copy = source.cloneNode(true);
    copy.removeAttribute("data-ck-marquee-source");
    copy.dataset.ckMarqueeCopy = "true";
    makeCloneInert(copy);
    track.append(copy);

    const style = getComputedStyle(track);
    const gap = Number.parseFloat(style.columnGap || style.gap) || 0;
    const distance = source.getBoundingClientRect().width + gap;
    track.style.setProperty("--ck-marquee-distance", `${distance}px`);
    const pixelsPerSecond = track.closest(".press-strip") ? 34 : track.closest(".instagram-journey") ? 38 : 30;
    track.style.setProperty("--ck-marquee-duration", `${Math.max(18, distance / pixelsPerSecond)}s`);
    marqueeRecords.set(track, true);
  }

  function hydrateMarquees(root = document) {
    selectIncludingRoot(root, ".testimonials__track, .consult-testimonials__track").forEach((track) => {
      const prepared = prepareExistingMarquee(track, "reverse");
      if (prepared) requestAnimationFrame(() => measureMarquee(prepared));
    });

    selectIncludingRoot(root, ".press-strip__track").forEach((track) => {
      const prepared = prepareExistingMarquee(track, "forward");
      if (prepared) requestAnimationFrame(() => measureMarquee(prepared));
    });

    selectIncludingRoot(root, ".home-benefits, .help-benefits, .swayam-benefits, .ring-benefits, .try-trust").forEach((viewport) => {
      const track = prepareFlatMarquee(viewport, "benefits", "forward");
      if (track) requestAnimationFrame(() => measureMarquee(track));
    });

    selectIncludingRoot(root, ".instagram-journey__feed").forEach((viewport) => {
      const track = prepareFlatMarquee(viewport, "instagram", "forward");
      if (track) requestAnimationFrame(() => measureMarquee(track));
    });
  }

  /* The floating WhatsApp launcher was mounted from here in the prototype, with
     its stylesheet and icon resolved relative to this script. In the theme it is
     snippets/whatsapp-launcher.liquid, rendered by the layout on every page, with
     the number and copy coming from theme settings and the app proxy. */

  function hydrate(root = document) {
    hydrateMotion(root);
    hydrateMarquees(root);
  }

  function refreshMarquees(root = document) {
    hydrateMarquees(root);
    requestAnimationFrame(() => marqueeRecords.forEach((_, track) => measureMarquee(track)));
  }

  window.CaratKinSiteExperience = { hydrate, refreshMarquees };
  document.documentElement.classList.add("ck-motion-ready");
  try {
    hydrate();
  } catch (error) {
    window.CaratKinSiteExperience.error = String(error);
    console.error(error);
  }

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) hydrate(node);
    }));
  });
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  window.addEventListener("resize", () => {
    window.clearTimeout(marqueeResizeTimer);
    marqueeResizeTimer = window.setTimeout(() => marqueeRecords.forEach((_, track) => measureMarquee(track)), 180);
  }, { passive: true });

  document.addEventListener("click", (event) => {
    const anchor = event.target.closest("a[href]");
    if (!anchor || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (anchor.target === "_blank" || anchor.hasAttribute("download") || anchor.matches("[data-cart-toggle], [data-auth-required]")) return;

    const destination = new URL(anchor.href, window.location.href);
    if (destination.origin !== window.location.origin || reducedMotion.matches) return;
    /* Prototype URLs ended in .html, which is how this gate used to read; on
       Shopify that test is never true, so the fade never played. Same-page
       anchors and identical URLs are still left alone. */
    if (destination.pathname === window.location.pathname && destination.hash) return;
    if (destination.href === window.location.href) return;

    event.preventDefault();
    document.documentElement.classList.add("ck-page-leaving");
    window.setTimeout(() => window.location.assign(destination.href), 140);
  });

})();
